import { Shield, AlertTriangle, CheckCircle, TrendingUp, TrendingDown, Loader2, Building2, Scale } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { usePrefetchOrFetch } from "../common/DataPrefetch";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export const PolicyInfluence = () => {
  // Use the shared prefetch store (bootstrap) so this panel renders instantly
  // when the user navigates in. Falls back to live endpoint if the stored
  // payload is missing `domains` / `total_oecd_papers` (e.g. legacy cache).
  const [data, loading] = usePrefetchOrFetch(
    "policyInfluence",
    `${API}/research/policy-influence`,
    {
      validate: (d) => d && Array.isArray(d.domains) && typeof d.total_oecd_papers === "number",
      delay: 500, // snappy fallback if store never delivers
    }
  );

  if (loading) return (
    <div className="flex items-center justify-center h-64" data-testid="policy-influence-loading">
      <Loader2 className="w-6 h-6 text-cyan-400 animate-spin" />
      <span className="ml-3 text-slate-400">Analyzing policy influence...</span>
    </div>
  );

  if (!data) return null;

  const chartData = data.domains
    .filter(d => d.policy_pct > 0 || d.product_pct > 2)
    .map(d => ({
      name: d.domain.replace("& ", "& \n").length > 20 ? d.domain.substring(0, 18) + "..." : d.domain,
      fullName: d.domain,
      policy: d.policy_pct,
      products: d.product_pct,
      gap: d.gap,
      status: d.status,
    }));

  const getBarColor = (gap) => {
    if (gap > 10) return "#22d3ee";
    if (gap > 0) return "#34d399";
    if (gap > -10) return "#fbbf24";
    return "#f87171";
  };

  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const d = payload[0]?.payload;
    return (
      <div className="bg-slate-800 border border-slate-600 rounded-lg p-3 text-xs shadow-xl">
        <p className="text-white font-semibold mb-1">{d?.fullName}</p>
        <p className="text-cyan-400">OECD Policy Focus: {d?.policy}%</p>
        <p className="text-purple-400">AI Product Share: {d?.products}%</p>
        <p className={d?.gap > 0 ? "text-emerald-400" : "text-amber-400"}>
          Gap: {d?.gap > 0 ? "+" : ""}{d?.gap}%
        </p>
      </div>
    );
  };

  return (
    <div className="space-y-6 p-4 md:p-6" data-testid="policy-influence">
      {/* Header */}
      <div className="text-center mb-6">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-500/10 border border-cyan-500/20 mb-3">
          <Shield className="w-3.5 h-3.5 text-cyan-400" />
          <span className="text-[11px] text-cyan-400 tracking-wider uppercase">Policy Influence Score</span>
        </div>
        <h2 className="text-xl md:text-2xl font-bold text-white mb-2">Where Policy Meets Innovation</h2>
        <p className="text-sm text-slate-400 max-w-xl mx-auto">
          Cross-referencing <span className="text-cyan-400 font-semibold">{data.total_oecd_papers}</span> OECD policy papers 
          against <span className="text-purple-400 font-semibold">{data.total_products?.toLocaleString()}</span> AI products 
          to reveal where policy attention leads or lags actual development.
        </p>
      </div>

      {/* Coverage Score */}
      <div className="flex justify-center">
        <div className="inline-flex items-center gap-6 px-6 py-3 bg-slate-800/60 border border-slate-700/50 rounded-xl">
          <div className="text-center">
            <p className="text-2xl font-bold text-cyan-400">{data.coverage_score}%</p>
            <p className="text-[10px] text-slate-500 uppercase">Policy Coverage</p>
          </div>
          <div className="w-px h-8 bg-slate-700" />
          <div className="text-center">
            <p className="text-2xl font-bold text-emerald-400">{data.covered_domains}</p>
            <p className="text-[10px] text-slate-500 uppercase">Domains Covered</p>
          </div>
          <div className="w-px h-8 bg-slate-700" />
          <div className="text-center">
            <p className="text-2xl font-bold text-amber-400">{data.under_watched?.length || 0}</p>
            <p className="text-[10px] text-slate-500 uppercase">Under-Watched</p>
          </div>
        </div>
      </div>

      {/* Gap Chart */}
      <div className="bg-slate-800/40 border border-slate-700/40 rounded-xl p-4">
        <h3 className="text-sm font-semibold text-white mb-1">Policy Attention Gap by Domain</h3>
        <p className="text-[10px] text-slate-500 mb-4">Positive gap = more OECD policy focus than product development. Negative = products outpace policy.</p>
        <ResponsiveContainer width="100%" height={320}>
          <BarChart data={chartData} layout="horizontal" margin={{ top: 5, right: 10, left: 10, bottom: 60 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
            <XAxis
              dataKey="name"
              tick={{ fill: "#94a3b8", fontSize: 9 }}
              angle={-35}
              textAnchor="end"
              interval={0}
              height={70}
            />
            <YAxis tick={{ fill: "#94a3b8", fontSize: 10 }} unit="%" />
            <Tooltip content={<CustomTooltip />} />
            <Bar dataKey="gap" radius={[4, 4, 0, 0]}>
              {chartData.map((entry) => (
                <Cell key={entry.name || entry.domain} fill={getBarColor(entry.gap)} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Domain Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {/* Over-watched */}
        <div className="bg-slate-800/40 border border-cyan-500/20 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-3">
            <TrendingUp className="w-4 h-4 text-cyan-400" />
            <h3 className="text-sm font-semibold text-white">Policy-Heavy Domains</h3>
          </div>
          <p className="text-[10px] text-slate-500 mb-3">More OECD policy attention than actual AI product development</p>
          <div className="space-y-2">
            {data.over_watched?.slice(0, 5).map((d) => (
              <div key={d.domain} className="flex items-center justify-between py-1.5 px-2 bg-slate-900/40 rounded">
                <span className="text-xs text-slate-300">{d.domain}</span>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-cyan-400">+{d.gap}%</span>
                  <CheckCircle className="w-3 h-3 text-cyan-400" />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Under-watched */}
        <div className="bg-slate-800/40 border border-amber-500/20 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-3">
            <TrendingDown className="w-4 h-4 text-amber-400" />
            <h3 className="text-sm font-semibold text-white">Under-Watched Domains</h3>
          </div>
          <p className="text-[10px] text-slate-500 mb-3">Product development outpaces OECD policy scrutiny</p>
          <div className="space-y-2">
            {data.under_watched?.length > 0 ? data.under_watched.slice(0, 5).map((d) => (
              <div key={d.domain} className="flex items-center justify-between py-1.5 px-2 bg-slate-900/40 rounded">
                <span className="text-xs text-slate-300">{d.domain}</span>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-amber-400">{d.gap}%</span>
                  <AlertTriangle className="w-3 h-3 text-amber-400" />
                </div>
              </div>
            )) : (
              <p className="text-[10px] text-slate-500 italic">All domains have adequate policy coverage</p>
            )}
          </div>
        </div>
      </div>

      {/* Full Domain Table */}
      <div className="bg-slate-800/40 border border-slate-700/40 rounded-xl p-4">
        <h3 className="text-sm font-semibold text-white mb-3">All Domain Influence Scores</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="policy-influence-table">
            <thead>
              <tr className="text-slate-500 border-b border-slate-700">
                <th className="text-left py-2 px-2">Domain</th>
                <th className="text-right py-2 px-2">OECD Papers</th>
                <th className="text-right py-2 px-2">Policy %</th>
                <th className="text-right py-2 px-2">Products</th>
                <th className="text-right py-2 px-2">Product %</th>
                <th className="text-right py-2 px-2">Gap</th>
                <th className="text-center py-2 px-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {data.domains.map((d) => (
                <tr key={d.domain} className="border-b border-slate-800/50 hover:bg-slate-700/20 transition-colors">
                  <td className="py-2 px-2 text-slate-300">{d.domain}</td>
                  <td className="py-2 px-2 text-right text-slate-400">{d.policy_papers}</td>
                  <td className="py-2 px-2 text-right text-cyan-400">{d.policy_pct}%</td>
                  <td className="py-2 px-2 text-right text-slate-400">{d.products.toLocaleString()}</td>
                  <td className="py-2 px-2 text-right text-purple-400">{d.product_pct}%</td>
                  <td className={`py-2 px-2 text-right font-medium ${d.gap > 0 ? "text-emerald-400" : "text-amber-400"}`}>
                    {d.gap > 0 ? "+" : ""}{d.gap}%
                  </td>
                  <td className="py-2 px-2 text-center">
                    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] ${
                      d.status === "over-watched" ? "bg-cyan-500/10 text-cyan-400" :
                      d.status === "under-watched" ? "bg-amber-500/10 text-amber-400" :
                      "bg-emerald-500/10 text-emerald-400"
                    }`}>
                      {d.status === "over-watched" ? <Scale className="w-2.5 h-2.5" /> :
                       d.status === "under-watched" ? <AlertTriangle className="w-2.5 h-2.5" /> :
                       <CheckCircle className="w-2.5 h-2.5" />}
                      {d.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Footer */}
      <div className="flex items-center gap-2 text-[10px] text-slate-500 justify-center pt-2">
        <Building2 className="w-3 h-3" />
        <span>Based on {data.total_oecd_papers} OECD Artificial Intelligence Papers cross-referenced with {data.total_products?.toLocaleString()} AI products</span>
      </div>
    </div>
  );
};
